import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase } from '../services/supabase'

// Get environment variables
const STRIPE_PRICES = {
  business: import.meta.env.VITE_STRIPE_BUSINESS_PRICE_ID,
  pro: import.meta.env.VITE_STRIPE_PRO_PRICE_ID
}

export const useSubscriptionStore = defineStore('subscription', () => {
  const currentTier = ref('free')
  const searchesUsed = ref(0)
  const contentCreated = ref(0)
  const searchesLimit = ref(5)
  const contentLimit = ref(5)
  const subscription = ref(null)

  async function initializeUser() {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      // First check user_subscriptions table for admin status
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('user_subscriptions')
        .select('tier, searches_used, content_creations_used')
        .eq('user_id', user.id)
        .single()

      if (subscriptionError && subscriptionError.code !== 'PGRST116') {
        console.error('Error checking subscription:', subscriptionError)
      }

      // If user is admin, set unlimited access
      if (subscriptionData?.tier === 'admin') {
        currentTier.value = 'admin'
        searchesUsed.value = 0
        contentCreated.value = 0
        searchesLimit.value = -1 // Unlimited
        contentLimit.value = -1 // Unlimited
        subscription.value = subscriptionData
        return
      }

      // If not admin, proceed with normal subscription check
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle()

      if (error && error.code !== 'PGRST116') {
        throw error
      }

      if (data) {
        currentTier.value = data.tier || 'free'
        searchesUsed.value = data.searches_used || 0
        contentCreated.value = data.content_creations_used || 0
        subscription.value = data

        // Set limits based on tier
        const limits = getTierLimits(data.tier)
        searchesLimit.value = limits.searches
        contentLimit.value = limits.content
      } else {
        // Reset to free tier if no subscription found
        currentTier.value = 'free'
        const limits = getTierLimits('free')
        searchesLimit.value = limits.searches
        contentLimit.value = limits.content
      }

    } catch (error) {
      console.error('Error initializing user:', error)
      // Reset to free tier on error
      currentTier.value = 'free'
      const limits = getTierLimits('free')
      searchesLimit.value = limits.searches
      contentLimit.value = limits.content
    }
  }

  function getTierLimits(tier) {
    switch (tier) {
      case 'admin':
        return { searches: -1, content: -1 } // Unlimited
      case 'pro':
        return { searches: -1, content: -1 } // Unlimited
      case 'business':
        return { searches: 100, content: 50 }
      case 'free':
      default:
        return { searches: 5, content: 5 }
    }
  }

  async function createCheckoutSession(plan) {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session) {
        throw new Error('User not authenticated')
      }

      const response = await fetch(`${window.location.origin}/api/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          priceId: STRIPE_PRICES[plan]
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to create checkout session')
      }

      const { url } = await response.json()
      if (!url) {
        throw new Error('No checkout URL received')
      }

      window.location.href = url
    } catch (error) {
      throw error
    }
  }

  async function createPortalSession() {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session) {
        throw new Error('User not authenticated')
      }

      const response = await fetch(`${window.location.origin}/api/customer-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'Accept': 'application/json'
        }
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Failed to create portal session: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      if (data.url) {
        window.location.href = data.url
      } else {
        throw new Error('No URL in response')
      }
    } catch (error) {
      throw error
    }
  }

  const canSearch = computed(() => {
    return searchesLimit.value === -1 || searchesUsed.value < searchesLimit.value
  })

  const canCreateContent = computed(() => {
    return contentLimit.value === -1 || contentCreated.value < contentLimit.value
  })

  async function checkAndResetUsage() {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      // Check if user is admin from user_subscriptions
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('user_subscriptions')
        .select('tier')
        .eq('user_id', user.id)
        .single()

      if (subscriptionError && subscriptionError.code !== 'PGRST116') {
        throw subscriptionError
      }

      const isAdmin = subscriptionData?.tier === 'admin'

      if (isAdmin) {
        currentTier.value = 'admin'
        searchesUsed.value = 0
        contentCreated.value = 0
        searchesLimit.value = -1
        contentLimit.value = -1
        return
      }

      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle()

      if (error && error.code !== 'PGRST116') {
        throw error
      }

      if (data) {
        searchesUsed.value = data.searches_used || 0
        contentCreated.value = data.content_creations_used || 0
        searchesLimit.value = getTierLimits(data.tier || 'free').searches
        contentLimit.value = getTierLimits(data.tier || 'free').content
      }
    } catch (error) {
      console.error('Error checking usage:', error)
      throw error
    }
  }

  async function incrementSearchUsage() {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return false

      if (!canSearch.value) return false

      const { error } = await supabase
        .from('user_subscriptions')
        .update({ searches_used: searchesUsed.value + 1 })
        .eq('user_id', user.id)

      if (error) throw error

      searchesUsed.value++
      return true
    } catch (error) {
      return false
    }
  }

  async function incrementContentCreationUsage() {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return false

      if (!canCreateContent.value) return false

      const { error } = await supabase
        .from('user_subscriptions')
        .update({ content_creations_used: contentCreated.value + 1 })
        .eq('user_id', user.id)

      if (error) throw error

      contentCreated.value++
      return true
    } catch (error) {
      return false
    }
  }

  async function checkSubscriptionStatus(sessionId) {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session) {
        throw new Error('No active session')
      }

      const response = await fetch(`${window.location.origin}/api/subscription-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ sessionId })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to check subscription status')
      }

      const data = await response.json()

      if (data.tier) {
        currentTier.value = data.tier
        searchesLimit.value = getTierLimits(data.tier).searches
        contentLimit.value = getTierLimits(data.tier).content
      }

      if (data.subscription) {
        subscription.value = {
          id: data.subscription.id,
          status: data.subscription.status,
          amount: data.subscription.amount,
          currency: data.subscription.currency,
          current_period_end: data.subscription.current_period_end,
          cancel_at_period_end: data.subscription.cancel_at_period_end
        }
      }

      return data
    } catch (error) {
      console.error('Error checking subscription status:', error)
      throw error
    }
  }

  // Watch for URL changes to detect successful checkout
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href)
    const sessionId = url.searchParams.get('session_id')
    
    if (sessionId) {
      setTimeout(() => {
        checkSubscriptionStatus(sessionId)
          .then(() => {
            url.searchParams.delete('session_id')
            window.history.replaceState({}, '', url.toString())
          })
          .catch(error => {
            setTimeout(() => {
              checkSubscriptionStatus(sessionId)
                .catch(() => {})
            }, 2000)
          })
      }, 1000)
    }
  }

  return {
    currentTier,
    searchesUsed,
    contentCreated,
    searchesLimit,
    contentLimit,
    subscription,
    canSearch,
    canCreateContent,
    initializeUser,
    createCheckoutSession,
    createPortalSession,
    checkAndResetUsage,
    incrementSearchUsage,
    incrementContentCreationUsage,
    checkSubscriptionStatus
  }
})
